import { ProcessGeneral } from 'api/endpoints/processes';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';

import {
  GeneralPayablesApprovalConfiguration,
  GeneralPayablesApprovalConfigurationBase,
  GeneralPayablesApprovalRule,
  GeneralPayablesVendorDepartment,
} from '../types';
import { IGeneralPayablesApprovalService } from './IGeneralPayablesApprovalService';
import {
  FindApproverRuleRequest,
  FindDepartmentsFromCompanyNameRequest,
  GeneralPayablesDepartmentAndApproverResponse,
  IsUserGPAdminResponse,
} from './typing';

export class GeneralPayablesApprovalService
  extends BaseRepo
  implements IGeneralPayablesApprovalService
{
  getConfiguration = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<GeneralPayablesApprovalConfiguration | undefined> => {
    const link = draft._links?.['getGeneralPayablesApprovalConfiguration'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "get General Payables approval configuration" is not available'
        )
      );
    }

    const getConfig =
      await this.helpers.handleGet<GeneralPayablesApprovalConfiguration>(
        link.href
      );
    if (isApiError(getConfig)) {
      if (getConfig.status == 404) {
        return undefined;
      }

      return getConfig;
    }
    return getConfig;
  };

  createConfiguration = async (
    draft: ProcessGeneral,
    request: GeneralPayablesApprovalConfigurationBase
  ): ApiResponseAsync<GeneralPayablesApprovalConfiguration> => {
    const link = draft._links?.['createGeneralPayablesApprovalConfiguration'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "create General Payables approval configuration" is not available'
        )
      );
    }

    return this.helpers.handlePost(link.href, request);
  };

  updateConfiguration = async (
    approvalConfig: GeneralPayablesApprovalConfiguration,
    request: GeneralPayablesApprovalConfigurationBase
  ): ApiResponseAsync<GeneralPayablesApprovalConfiguration> => {
    const link = approvalConfig._links?.['update'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "update General Payables approval configuration" is not available'
        )
      );
    }

    return this.helpers.handlePut(link.href, request);
  };

  deleteConfiguration = async (
    approvalConfig: GeneralPayablesApprovalConfiguration
  ): ApiResponseAsync<void> => {
    const link = approvalConfig._links?.['delete'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "delete General Payables approval configuration" is not available'
        )
      );
    }

    return this.helpers.handleDelete(link.href);
  };

  getDepartmentsAndApprovers = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<GeneralPayablesDepartmentAndApproverResponse[]> => {
    const link =
      draft._links?.['getGeneralPayablesApprovalDepartmentsAndApprovers'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "get General Payables departments and approvers" is not available'
        )
      );
    }

    return this.helpers.handleGet(link.href);
  };

  findDepartmentsFromCompanyName = async (
    draft: ProcessGeneral,
    companyName: string
  ): ApiResponseAsync<GeneralPayablesVendorDepartment[]> => {
    const link =
      draft._links?.['findGeneralPayablesApprovalDepartmentsByCompany'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "get General Payables departments and approvers" is not available'
        )
      );
    }

    const request: FindDepartmentsFromCompanyNameRequest = { companyName };

    return this.helpers.handlePost(link.href, request);
  };

  findApproverRule = async (
    draft: ProcessGeneral,
    department: string
  ): ApiResponseAsync<GeneralPayablesApprovalRule> => {
    const link = draft._links?.['findGeneralPayablesApproverRule'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "find General Payables approval rule" is not available')
      );
    }

    const response = await this.helpers.handlePost<
      FindApproverRuleRequest,
      GeneralPayablesApprovalRule
    >(link.href, { department });
    if (isApiError(response)) {
      if (response.status == 404) {
        return {
          id: '',
          approver: '',
          department: '',
          approvalLimit: 0,
          isInitial: false,
        };
      }

      return response;
    }
    return response;
  };

  isUserGPAdmin = async (
    draft: ProcessGeneral
  ): ApiResponseAsync<IsUserGPAdminResponse> => {
    const link = draft._links?.['isGeneralPayablesApproverAdmin'];

    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "user is General Payables approval admin" is not available'
        )
      );
    }

    return this.helpers.handleGet(link.href);
  };
}
